import { PropertyType } from '../types/property';
import { BareSailing, BareSailingDay } from '../types/sailing';

/**
 * Configuration options for Properties to discriminate between ship / land
 *
 */

interface PropertyConfigBase {
  // Label for itinerary (Itinierary/Calendar/...)
  itineraryLabel: string;
  // Label for sailing (Sailing/Week/...)
  sailingLabel: string;
  // Label for Berthing in Staff Dialogs
  berthingLabel: string;
  // Label for location (day)
  locationLabel: string;
  // Label for levels (deck/floor)
  levelLabel: string;
  // Whether to show the venue location next to the venue level (aft/mid/...)
  showLocation: boolean;
  // Whether to use a calendar view instead of a list of sailings
  useCalendarView: boolean;
  // Whether to show the port names in days
  showPort: boolean;
  // Whether to show the plan type (port/sea/...)
  showType: boolean;
  // Label for the group leader
  groupLeaderLabel: string;
  // Label for the group leader room number
  groupLeaderRoomLabel: string;
  // Whether the `homecoming` event template option should be displayed
  allowHomecomingEvents: boolean;
  // Whether the `allAboard` event template option should be displayed
  allowAllAboardEvents: boolean;
  // Whether we should display options related to DDP
  showDigitalDailyProgramOptions: boolean;
}

interface PropertyConfigShip extends PropertyConfigBase {
  // Function to format day names
  dayNameFormatter: (sailing: BareSailing, sailingDay: BareSailingDay) => string;
}

interface PropertyConfigShore extends PropertyConfigBase {
  // Function to format day names
  dayNameFormatter: (sailing: BareSailing, sailingDay: BareSailingDay) => string;
}

type PropertyConfigReturnType<T> = T extends 'sea'
  ? PropertyConfigShip
  : T extends 'shore'
  ? PropertyConfigShore
  : never;

type PropertyConfigName = PropertyType;

export function getPropertyConfiguration<
  T extends PropertyConfigName,
  K extends keyof PropertyConfigReturnType<T>
>(propertyType: T, key: K): PropertyConfigReturnType<T>[K] {
  if (propertyType === 'sea') {
    return (ShipPropertyConfig as PropertyConfigReturnType<T>)[key];
  } else {
    return (ShorePropertyConfig as PropertyConfigReturnType<T>)[key];
  }
}

const ShipPropertyConfig: PropertyConfigShip = {
  itineraryLabel: 'Itinerary',
  sailingLabel: 'Sailing',
  berthingLabel: 'Berthing',
  levelLabel: 'Deck',
  locationLabel: 'Location',
  showLocation: true,
  useCalendarView: false,
  showPort: true,
  showType: true,
  dayNameFormatter: (sailing: BareSailing, day: BareSailingDay) =>
    `${sailing.name} (Day ${day.name})`,
  groupLeaderLabel: 'Tour Leader Onboard',
  groupLeaderRoomLabel: 'Tour Leader Cabin #',
  allowHomecomingEvents: true,
  allowAllAboardEvents: true,
  showDigitalDailyProgramOptions: false,
};

const ShorePropertyConfig: PropertyConfigShore = {
  itineraryLabel: 'Calendar',
  sailingLabel: 'Week',
  berthingLabel: 'Room Accomodation',
  levelLabel: 'Floor',
  locationLabel: 'Day',
  showLocation: false,
  useCalendarView: true,
  showPort: false,
  showType: false,
  dayNameFormatter: (_: BareSailing, day: BareSailingDay) => `${day.name}`,
  groupLeaderLabel: 'Site Leader',
  groupLeaderRoomLabel: 'Site Leader Room #',
  allowHomecomingEvents: false,
  allowAllAboardEvents: false,
  showDigitalDailyProgramOptions: true,
};
